// Attendance policy report formulas — the web copy of the server's
// helpers/modules/AttendancePolicy/policyFormula.js, for the editor's live
// checks and preview. The server checks again on save and is the one that
// counts in reports, so keep the two in step (same names, same rules).
//
// Language: numbers, variables, + - * /, comparisons (1 / 0), brackets and
// the functions below. Empty (null) spreads; a report shows it as "—".

export const ROW_VARIABLES: Record<string, string> = {
    sessions_on: "Sessions held while the student was on the roll",
    sessions_present: "Sessions marked present",
    sessions_absent: "Sessions marked absent",
    sessions_leave: "Sessions marked leave",
    days_on: "Days with at least one session",
    days_full_present: "Days present in every session",
    days_any_present: "Days present in at least one session",
    slots_per_day: "Slots in a day (1 when there are none)",
    percent: "The standard attendance %",
    minimum_percent: "The policy's minimum % (empty when there is none)",
};

export const HEADER_VARIABLES: Record<string, string> = {
    students: "Students in the report",
    sessions_held: "Sessions held in the period",
    days_held: "Days with at least one session",
    minimum_percent: "The policy's minimum % (empty when there is none)",
};

export const ROW_FUNCTIONS: Record<string, [ number, number, string ]> = {
    round: [ 1, 2, "round(x) or round(x, decimals)" ],
    floor: [ 1, 1, "floor(x) — down to a whole number" ],
    ceil: [ 1, 1, "ceil(x) — up to a whole number" ],
    abs: [ 1, 1, "abs(x) — without the minus" ],
    min: [ 2, 10, "min(a, b, …) — the smallest" ],
    max: [ 2, 10, "max(a, b, …) — the largest" ],
    if: [ 3, 3, "if(condition, then, else)" ],
    and: [ 2, 10, "and(a, b, …) — 1 when all are true" ],
    or: [ 2, 10, "or(a, b, …) — 1 when any is true" ],
    not: [ 1, 1, "not(a)" ],
    empty: [ 1, 1, "empty(x) — 1 when x is empty" ],
    ifempty: [ 2, 2, "ifempty(x, fallback)" ],
};

export const AGGREGATES: Record<string, string> = {
    sum: "sum(x) — total over all students",
    avg: "avg(x) — average over all students",
    count: "count(condition) — students for whom it is true",
    minof: "minof(x) — the lowest",
    maxof: "maxof(x) — the highest",
};

export type FieldFormat = "number" | "percent" | "yesno";
export type ReportField = { key: string; name: string; formula: string; format: FieldFormat; decimals: number };

export const MAX_COLUMNS = 20;
export const MAX_HEADERS = 10;
const MAX_LENGTH = 300;
const MAX_DEPTH = 25;
const MAX_TOKENS = 150;
export const KEY = /^[a-z][a-z0-9_]{0,29}$/;
export const RESERVED = new Set([ ...Object.keys(ROW_VARIABLES), ...Object.keys(HEADER_VARIABLES), ...Object.keys(ROW_FUNCTIONS), ...Object.keys(AGGREGATES) ]);

export class FormulaError extends Error {}

type Tok = { t: "num"; v: number } | { t: "id"; v: string } | { t: "op"; v: string };
type Node =
    | { t: "num"; v: number }
    | { t: "var"; n: string }
    | { t: "neg"; a: Node }
    | { t: "bin"; op: string; a: Node; b: Node }
    | { t: "call"; f: string; args: Node[] };

function tokenize(src: string): Tok[] {
    const tokens: Tok[] = [];
    let i = 0;
    while (i < src.length) {
        const c = src[ i ];
        if (/\s/.test(c)) { i++; continue; }
        if (/[0-9.]/.test(c)) {
            const m = /^(\d+(\.\d+)?|\.\d+)/.exec(src.slice(i));
            if (!m) throw new FormulaError(`bad number at position ${i + 1}`);
            tokens.push({ t: "num", v: Number(m[ 0 ]) });
            i += m[ 0 ].length;
            continue;
        }
        if (/[A-Za-z_]/.test(c)) {
            const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
            tokens.push({ t: "id", v: m[ 0 ].toLowerCase() });
            i += m[ 0 ].length;
            continue;
        }
        const two = src.slice(i, i + 2);
        if ([ "<=", ">=", "!=", "==" ].includes(two)) { tokens.push({ t: "op", v: two === "==" ? "=" : two }); i += 2; continue; }
        if ("+-*/<>=(),".includes(c)) { tokens.push({ t: "op", v: c }); i++; continue; }
        throw new FormulaError(`"${c}" is not allowed (position ${i + 1})`);
    }
    if (tokens.length > MAX_TOKENS) throw new FormulaError("formula is too long");
    return tokens;
}

export function parse(src: string): Node {
    if (typeof src !== "string" || !src.trim()) throw new FormulaError("formula is empty");
    if (src.length > MAX_LENGTH) throw new FormulaError(`formula is longer than ${MAX_LENGTH} characters`);
    const tokens = tokenize(src);
    let pos = 0;
    const peek = () => tokens[ pos ];
    const isOp = (v: string) => peek()?.t === "op" && peek().v === v;
    const expect = (v: string) => {
        if (!isOp(v)) throw new FormulaError(`expected "${v}"${peek() ? ` before "${peek().v}"` : " at the end"}`);
        pos++;
    };
    const comparison = (depth: number): Node => {
        const left = additive(depth);
        const op = peek();
        if (op?.t === "op" && [ "<", "<=", ">", ">=", "=", "!=" ].includes(op.v)) {
            pos++;
            return { t: "bin", op: op.v, a: left, b: additive(depth) };
        }
        return left;
    };
    const additive = (depth: number): Node => {
        let node = term(depth);
        while (isOp("+") || isOp("-")) { const op = String(tokens[ pos++ ].v); node = { t: "bin", op, a: node, b: term(depth) }; }
        return node;
    };
    const term = (depth: number): Node => {
        let node = unary(depth);
        while (isOp("*") || isOp("/")) { const op = String(tokens[ pos++ ].v); node = { t: "bin", op, a: node, b: unary(depth) }; }
        return node;
    };
    const unary = (depth: number): Node => {
        if (depth > MAX_DEPTH) throw new FormulaError("formula is nested too deeply");
        if (isOp("-")) { pos++; return { t: "neg", a: unary(depth + 1) }; }
        if (isOp("+")) { pos++; return unary(depth + 1); }
        return primary(depth);
    };
    const primary = (depth: number): Node => {
        const tok = peek();
        if (!tok) throw new FormulaError("formula ends too early");
        if (tok.t === "num") { pos++; return { t: "num", v: tok.v }; }
        if (tok.t === "id") {
            pos++;
            if (isOp("(")) {
                pos++;
                const args: Node[] = [];
                if (!isOp(")")) {
                    args.push(comparison(depth + 1));
                    while (isOp(",")) { pos++; args.push(comparison(depth + 1)); }
                }
                expect(")");
                return { t: "call", f: tok.v, args };
            }
            return { t: "var", n: tok.v };
        }
        if (isOp("(")) {
            pos++;
            const inner = comparison(depth + 1);
            expect(")");
            return inner;
        }
        throw new FormulaError(`unexpected "${tok.v}"`);
    };
    const tree = comparison(0);
    if (pos < tokens.length) throw new FormulaError(`unexpected "${tokens[ pos ].v}"`);
    return tree;
}

type Scope = { kind: "row" | "header"; names: Set<string>; rowNames?: Set<string> };

export function check(tree: Node, scope: Scope): Node {
    const visit = (n: Node, inAgg: boolean): void => {
        switch (n.t) {
            case "num": return;
            case "neg": return visit(n.a, inAgg);
            case "bin": visit(n.a, inAgg); visit(n.b, inAgg); return;
            case "var": {
                const names = inAgg ? scope.rowNames! : scope.names;
                if (!names.has(n.n)) {
                    if (scope.kind === "header" && !inAgg && scope.rowNames?.has(n.n)) {
                        throw new FormulaError(`"${n.n}" is per student: use it inside sum(), avg(), count(), minof() or maxof()`);
                    }
                    throw new FormulaError(`unknown name "${n.n}"`);
                }
                return;
            }
            case "call": {
                if (AGGREGATES[ n.f ] !== undefined) {
                    if (scope.kind !== "header") throw new FormulaError(`${n.f}() only works in header fields`);
                    if (inAgg) throw new FormulaError(`${n.f}() can't be inside another sum/avg/count`);
                    if (n.args.length !== 1) throw new FormulaError(`${n.f}() takes one value`);
                    return visit(n.args[ 0 ], true);
                }
                const arity = ROW_FUNCTIONS[ n.f ];
                if (!arity) throw new FormulaError(`unknown function "${n.f}"`);
                if (n.args.length < arity[ 0 ] || n.args.length > arity[ 1 ]) {
                    const want = arity[ 0 ] === arity[ 1 ] ? `${arity[ 0 ]}` : `${arity[ 0 ]} to ${arity[ 1 ]}`;
                    throw new FormulaError(`${n.f}() takes ${want} value${arity[ 1 ] === 1 ? "" : "s"}`);
                }
                n.args.forEach(a => visit(a, inAgg));
            }
        }
    };
    visit(tree, false);
    return tree;
}

// Anything that isn't a finite number reads as empty (names, labels, …).
type Vars = Record<string, unknown>;
const isNum = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

export function evaluate(tree: Node, vars: Vars, rows: Vars[] | null = null): number | null {
    const ev = (n: Node, sv: Vars): number | null => {
        switch (n.t) {
            case "num": return n.v;
            case "var": { const v = sv[ n.n ]; return isNum(v) ? v : null; }
            case "neg": { const a = ev(n.a, sv); return a === null ? null : -a; }
            case "bin": {
                const a = ev(n.a, sv), b = ev(n.b, sv);
                if (a === null || b === null) return null;
                switch (n.op) {
                    case "+": return a + b;
                    case "-": return a - b;
                    case "*": return a * b;
                    case "/": return b === 0 ? null : a / b;
                    case "<": return a < b ? 1 : 0;
                    case "<=": return a <= b ? 1 : 0;
                    case ">": return a > b ? 1 : 0;
                    case ">=": return a >= b ? 1 : 0;
                    case "=": return Math.abs(a - b) < 1e-9 ? 1 : 0;
                    case "!=": return Math.abs(a - b) < 1e-9 ? 0 : 1;
                }
                return null;
            }
            case "call": {
                if (AGGREGATES[ n.f ] !== undefined) {
                    const nums = (rows || []).map(r => ev(n.args[ 0 ], r)).filter((v): v is number => v !== null);
                    switch (n.f) {
                        case "sum": return nums.reduce((s, v) => s + v, 0);
                        case "avg": return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : null;
                        case "count": return nums.filter(v => v !== 0).length;
                        case "minof": return nums.length ? Math.min(...nums) : null;
                        case "maxof": return nums.length ? Math.max(...nums) : null;
                    }
                }
                if (n.f === "if") {
                    const c = ev(n.args[ 0 ], sv);
                    if (c === null) return null;
                    return ev(c !== 0 ? n.args[ 1 ] : n.args[ 2 ], sv);
                }
                if (n.f === "empty") return ev(n.args[ 0 ], sv) === null ? 1 : 0;
                if (n.f === "ifempty") { const a = ev(n.args[ 0 ], sv); return a === null ? ev(n.args[ 1 ], sv) : a; }
                const args = n.args.map(a => ev(a, sv));
                if (args.some(a => a === null)) return null;
                const x = args as number[];
                switch (n.f) {
                    case "round": {
                        const d = x.length > 1 ? Math.max(0, Math.min(6, Math.trunc(x[ 1 ]))) : 0;
                        const f = 10 ** d;
                        return Math.round(x[ 0 ] * f) / f;
                    }
                    case "floor": return Math.floor(x[ 0 ]);
                    case "ceil": return Math.ceil(x[ 0 ]);
                    case "abs": return Math.abs(x[ 0 ]);
                    case "min": return Math.min(...x);
                    case "max": return Math.max(...x);
                    case "and": return x.every(a => a !== 0) ? 1 : 0;
                    case "or": return x.some(a => a !== 0) ? 1 : 0;
                    case "not": return x[ 0 ] === 0 ? 1 : 0;
                }
                return null;
            }
        }
    };
    const out = ev(tree, vars);
    return isNum(out) ? out : null;
}

// One message per field (or null when fine), the same checks the server
// runs on save. `columns` come first: header fields may use their keys.
export function fieldProblems(columns: ReportField[], headers: ReportField[]): { columns: (string | null)[]; headers: (string | null)[] } {
    const keys = new Set<string>();
    const run = (list: ReportField[], kind: "row" | "header") => list.map((f, i) => {
        const problems: string[] = [];
        if (!f.name.trim() || f.name.trim().length > 40) problems.push("name is required (up to 40 characters)");
        if (!KEY.test(f.key)) problems.push("key must start with a letter and use only a–z, 0–9 and _");
        else if (RESERVED.has(f.key)) problems.push(`"${f.key}" is a built-in name`);
        else if (keys.has(f.key)) problems.push(`key "${f.key}" is used twice`);
        try {
            const tree = parse(f.formula);
            if (kind === "row") {
                check(tree, { kind: "row", names: new Set([ ...Object.keys(ROW_VARIABLES), ...list.slice(0, i).map(c => c.key) ]) });
            } else {
                check(tree, {
                    kind: "header",
                    names: new Set([ ...Object.keys(HEADER_VARIABLES), ...list.slice(0, i).map(h => h.key) ]),
                    rowNames: new Set([ ...Object.keys(ROW_VARIABLES), ...columns.map(c => c.key) ]),
                });
            }
        } catch (err) {
            problems.push(err instanceof FormulaError ? err.message : "formula could not be read");
        }
        if (KEY.test(f.key)) keys.add(f.key);
        return problems.length ? problems.join(" · ") : null;
    });
    return { columns: run(columns, "row"), headers: run(headers, "header") };
}

// Values for a preview (fields assumed valid; a bad one gives empty values).
export function computeReport(columns: ReportField[], headers: ReportField[], students: Vars[], totals: Vars) {
    const safe = (formula: string) => { try { return parse(formula); } catch { return null; } };
    const colTrees = columns.map(c => [ c.key, safe(c.formula) ] as const);
    const rowVars = students.map(s => {
        const vars: Vars = { ...s };
        for (const [ key, tree ] of colTrees) vars[ key ] = tree ? evaluate(tree, vars) : null;
        return vars;
    });
    const headerValues: Record<string, number | null> = {};
    const headerVars: Vars = { ...totals };
    for (const h of headers) {
        const tree = safe(h.formula);
        headerValues[ h.key ] = tree ? evaluate(tree, headerVars, rowVars) : null;
        headerVars[ h.key ] = headerValues[ h.key ];
    }
    return { rows: rowVars, headers: headerValues };
}

export function formatValue(value: number | null | undefined, format: FieldFormat = "number", decimals = 0): string {
    if (value === null || value === undefined) return "—";
    if (format === "yesno") return value !== 0 ? "Yes" : "No";
    const text = value.toFixed(decimals);
    return format === "percent" ? `${text}%` : text;
}

// A key from a name: "Short by (sessions)" → "short_by_sessions".
export function keyFromName(name: string, taken: Set<string>): string {
    let base = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 26);
    if (!/^[a-z]/.test(base)) base = `c_${base}`.slice(0, 26);
    if (!base || base === "c_") base = "field";
    let key = base, n = 2;
    while (taken.has(key) || RESERVED.has(key)) key = `${base}_${n++}`;
    return key;
}
